import { IconButton } from '@material-ui/core'
import NotificationsNoneRounded from '@material-ui/icons/NotificationsNoneRounded'
import { authSelectors } from 'ducks/auth'
import { fetchOverdueStatus, overdueActionsSelectors } from 'ducks/overdueActions'
import { viewModeActions, viewModeSelectors } from 'ducks/viewMode'
import { COLOR_PALETTE } from 'elements/input/styles'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { BaseTheme, styled } from 'opensolar-ui'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionType } from 'types/tracking'

interface ActivityButtonIconProps {
  variant?: 'default' | 'navigation'
}

const DefaultIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isOverdue',
})<{ isOverdue: boolean }>(({ isOverdue }) => ({
  color: isOverdue ? COLOR_PALETTE.red2 : 'inherit',
  position: 'relative',
  padding: 8,
}))

const DefaultNotificationDot = styled('span')({
  position: 'absolute',
  top: 6,
  right: 8,
  width: 8,
  height: 8,
  backgroundColor: COLOR_PALETTE.red2,
  borderRadius: '50%',
})

const NavigationIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isOverdue',
})<{ isOverdue: boolean }>(({ isOverdue }) => ({
  color: isOverdue ? COLOR_PALETTE.red2 : BaseTheme.palette.grey[810],
  fontSize: 12,
  fontWeight: 700,
  gap: 6,
  padding: 8,
  position: 'relative',
  borderRadius: 8,
  '&:hover': {
    color: isOverdue ? COLOR_PALETTE.red2 : 'black',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& svg': {
    height: 20,
    width: 20,
  },
}))
const NavigationNotificationDot = styled('span')({
  position: 'absolute',
  top: 6,
  right: 6,
  width: 8,
  height: 8,
  backgroundColor: '#f44336',
  borderRadius: '50%',
  pointerEvents: 'none',
})

const ActivityButtonIcon: React.FC<ActivityButtonIconProps> = ({ variant = 'default' }) => {
  const notificationRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)
  const overdueActionsPresent = useSelector(overdueActionsSelectors.getHasOverdue)
  const isLoading = useSelector(overdueActionsSelectors.getIsLoading)
  const shouldRefresh = useSelector((state: any) => overdueActionsSelectors.shouldRefresh(state))
  const homeDrawerState = useSelector(viewModeSelectors.homeDrawerState)
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'acitivity_center_icon',
    eventName: 'User Viewed Activity Center',
  })

  useEffect(() => {
    if (orgId && shouldRefresh) {
      dispatch(fetchOverdueStatus(String(orgId)))
    }
  }, [dispatch, orgId, shouldRefresh])

  const handleNotificationClick = () => {
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        eventName: 'Clicked Activity Opened',
        source: 'activity_center_icon',
      }
    )
    if (homeDrawerState === 'activity') {
      dispatch(viewModeActions.setHomeDrawerState(undefined))
    } else {
      dispatch(viewModeActions.setHomeDrawerState('activity'))
    }

    if (homeDrawerState !== 'activity' && orgId) {
      dispatch(fetchOverdueStatus(String(orgId)))
    }
  }

  return (
    <div ref={notificationRef} data-side-panel-trigger="activity">
      {variant === 'default' ? (
        <DefaultIconButton
          isOverdue={overdueActionsPresent}
          onClick={handleNotificationClick}
          aria-label="notifications"
          disabled={isLoading}
          size="medium"
        >
          <NotificationsNoneRounded />
          {overdueActionsPresent && <DefaultNotificationDot />}
        </DefaultIconButton>
      ) : (
        <NavigationIconButton
          isOverdue={overdueActionsPresent}
          onClick={handleNotificationClick}
          aria-label="notifications"
          disabled={isLoading}
          size="small"
        >
          <NotificationsNoneRounded />
          {overdueActionsPresent && <NavigationNotificationDot />}
        </NavigationIconButton>
      )}
    </div>
  )
}

export default ActivityButtonIcon
